// moms-harness:anchor v2
// Spec: docs/spec/auth/SPEC-auth-okta-account-switch-redirect-A.yaml

import { OktaAuth, Token } from '@okta/okta-auth-js'
import { AuthStorage } from '@/utils/storage/AuthStorage'

/**
 * LOGISPRD-7099 — Okta SDK.
 *
 * Okta 설정(issuer/clientId)이 주입된 환경에서만 인스턴스 생성.
 *
 * LOGISPRD-11076: 로그인 방식을 가르던 서버 feature flag 분기는 제거됐다. 로그인 화면은
 * Okta 랜딩 하나뿐이며, 인스턴스가 없으면(설정 미주입) 로그인 자체가 성립하지 않는다.
 * 로그인 흐름은 Okta PKCE + autoRenew(refresh token) 자동 갱신 단일 경로다.
 */
const issuer = process.env.REACT_APP_OKTA_ISSUER
const clientId = process.env.REACT_APP_OKTA_CLIENT_ID

export const oktaAuth: OktaAuth | null =
	issuer && clientId
		? new OktaAuth({
				issuer,
				clientId,
				redirectUri: `${window.location.origin}/login/callback`,
				postLogoutRedirectUri: `${window.location.origin}/login`,
				scopes: ['openid', 'profile', 'email', 'offline_access'], // offline_access = refresh token
				pkce: true,
				// 만료 120초 전 자동 갱신 (백그라운드 autoRenew 서비스 + 요청 시 getOrRenew 보강)
				tokenManager: { autoRenew: true, expireEarlySeconds: 120 },
				services: { autoRenew: true },
			})
		: null

/** 현재 환경에 Okta 설정(issuer/clientId)이 주입되어 있는지. */
export const isOktaConfigured = (): boolean => oktaAuth !== null

/** 보관 중인 Okta access token (동기 read, 없으면 undefined). request/logout 분기 기준. */
export const getOktaAccessToken = (): string | undefined => oktaAuth?.getAccessToken()

interface OktaTokenSnapshot {
	tokenValid: boolean
	accessTokenPresent: boolean
	refreshTokenPresent: boolean
}

const REAUTH_INFLIGHT_KEY = 'oktaReauthInflight'
const AUTH_ENTRY_PATH_PREFIXES = ['/login', '/auth']

let renewPromise: Promise<string | undefined> | undefined
let reauthenticationPromise: Promise<void> | undefined

const getOktaTokenSnapshot = (): OktaTokenSnapshot => {
	let tokenValid = false
	let accessTokenPresent = false
	let refreshTokenPresent = false

	try {
		const tokens = oktaAuth?.tokenManager.getTokensSync()
		accessTokenPresent = Boolean(tokens?.accessToken?.accessToken)
		refreshTokenPresent = Boolean(tokens?.refreshToken?.refreshToken)
		if (tokens?.accessToken) tokenValid = !oktaAuth?.tokenManager.hasExpired(tokens.accessToken)
	} catch {
		// Missing is the fail-safe state.
	}

	return { tokenValid, accessTokenPresent, refreshTokenPresent }
}

/** 요청/부트스트랩 시점 호출 — 만료 임박 토큰만 single-flight로 갱신한다. */
export const getOrRenewOktaAccessToken = async (): Promise<string | undefined> => {
	if (!oktaAuth) return undefined

	const snapshot = getOktaTokenSnapshot()
	if (snapshot.tokenValid) return getOktaAccessToken()
	if (renewPromise) return renewPromise
	if (!snapshot.accessTokenPresent && !snapshot.refreshTokenPresent) return undefined

	renewPromise = oktaAuth
		.getOrRenewAccessToken()
		.then((token) => {
			if (!token) return undefined
			AuthStorage.accessToken.set(token)
			return token
		})
		.catch(() => undefined)
		.finally(() => {
			renewPromise = undefined
		})

	return renewPromise
}

/** autoRenew 백그라운드 서비스 가동. */
export const ensureOktaAutoRenew = async (): Promise<void> => {
	if (!oktaAuth) return
	await oktaAuth.start().catch(() => undefined)
}

/** 자체 로그인 폼 대신 Okta 로그인 화면으로 redirect (PKCE). */
export const oktaLoginRedirect = async (): Promise<void> => {
	await oktaAuth?.signInWithRedirect()
}

/** 동시다발 만료 요청을 하나의 Okta redirect로 합친다. 실패 시에만 가드를 풀어 재시도를 허용한다. */
export const reauthenticateWithOktaOnce = (): Promise<void> => {
	if (sessionStorage.getItem(REAUTH_INFLIGHT_KEY) === '1') {
		return reauthenticationPromise ?? Promise.resolve()
	}

	sessionStorage.setItem(REAUTH_INFLIGHT_KEY, '1')
	const redirectPromise = oktaLoginRedirect()
	reauthenticationPromise = redirectPromise
		.catch((error) => {
			sessionStorage.removeItem(REAUTH_INFLIGHT_KEY)
			throw error
		})
		.finally(() => {
			reauthenticationPromise = undefined
		})
	return reauthenticationPromise
}

/** Okta 세션의 reload에서 auto-renew와 토큰 검증을 iframe 렌더보다 먼저 완료한다. */
export const prepareOktaSessionBeforeRender = async (): Promise<boolean> => {
	const isAuthEntryPath = AUTH_ENTRY_PATH_PREFIXES.some((prefix) => globalThis.location.pathname.startsWith(prefix))
	if (!oktaAuth || AuthStorage.authMode.get() !== 'okta' || isAuthEntryPath) return true

	await ensureOktaAutoRenew()
	const accessToken = await getOrRenewOktaAccessToken()
	if (accessToken) {
		AuthStorage.accessToken.set(accessToken)
		return true
	}

	AuthStorage.accessToken.clear()
	try {
		await reauthenticateWithOktaOnce()
		return false
	} catch {
		return true
	}
}

/**
 * LOGISPRD-9129 — Okta 세션 종료 → postLogoutRedirectUri 로 이동 (SDK 토큰/autoRenew 완전 정리).
 * stop-first → clearTokensBeforeRedirect 순서로 강화: autoRenew 정지(M3) 후 redirect "전" 토큰 clear(M2).
 *  - stop(): autoRenew 백그라운드 서비스 정지 → 만료 임박 토큰의 신규 renew 스케줄링 차단(방어). call order(stop→signOut).
 *    (한계: 이미 in-flight 인 renewPromise 는 stop 이 취소하지 못함 — 극단 레이스에서 1회 재생성 가능하나 signOut clear + 즉시 redirect(unload)로 영향 최소.)
 *  - signOut({ clearTokensBeforeRedirect: true }): redirect 전 tokenManager.clear() 보장(localStorage okta-token-storage 잔존 제거).
 *  - stop reject 는 best-effort swallow(Q2): try/catch 로 삼켜(optional chaining 결과에 .catch 직접 체이닝 불가) signOut 진행.
 *  - signOut reject 는 전파(9057 인터셉터 가드가 관찰 — silent 성공 위장 금지).
 * Spec: docs/spec/auth/okta-logout-cleanup.yaml (REQ-1)
 * LLD anchor: docs/lld/auth/LLD-9129-okta-logout-cleanup.md "## 외부 경계 인터페이스" (2), "## 핵심 로직 (의사 코드)", "## 에러 정책".
 * JIRA: LOGISPRD-9129 / Assignee: hyeongjin.na
 */
export const oktaLogout = async (): Promise<void> => {
	// 1) autoRenew 정지(재생성 차단) — best-effort swallow(Q2): 정지 실패가 로그아웃을 막지 않게 try/catch.
	try {
		await oktaAuth?.stop()
	} catch (e) {
		// swallow: stop 실패해도 signOut 진행(best-effort). 단 관측성 위해 debug 로깅(운영 추적).
		console.debug('[okta] stop() failed during logout (ignored, best-effort)', e)
	}
	// 2) clear(+revoke) → redirect. reject 는 전파(9057 인터셉터 가드 관찰).
	await oktaAuth?.signOut({ clearTokensBeforeRedirect: true })
}

/**
 * LOGISPRD-10263 — 앱 미할당 callback에는 ID token이 없으므로 SDK signOut의
 * cross-origin closeSession fallback을 사용하지 않고 Okta top-level redirect URL을 준비한다.
 * 정상 Okta 로그아웃은 revoke를 포함하는 oktaLogout 경로를 그대로 유지한다.
 */
export const prepareOktaAccountSwitchRedirect = async (): Promise<string> => {
	if (!oktaAuth || !issuer) throw new Error('Okta is not configured')

	const logoutUrl = new URL('/login/signout', issuer)
	logoutUrl.searchParams.set('fromURI', new URL('/login', globalThis.location.origin).href)

	try {
		await oktaAuth.stop()
	} catch {
		// 계정 전환 redirect를 막지 않는 best-effort 정리다.
	}

	const cleanupActions = [
		() => oktaAuth.tokenManager.clear(),
		() => AuthStorage.accessToken.clear(),
		() => AuthStorage.authMode.clear(),
		() => globalThis.sessionStorage.removeItem(REAUTH_INFLIGHT_KEY),
	]
	cleanupActions.forEach((cleanup) => {
		try {
			cleanup()
		} catch {
			// 콜백에서 이미 정리한 상태의 방어적 cleanup이므로 redirect는 계속한다.
		}
	})

	return logoutUrl.href
}

/** /login/callback 에서 호출 — code 교환 후 토큰을 tokenManager 에 저장. */
export const handleOktaCallback = async (): Promise<void> => {
	await oktaAuth?.handleLoginRedirect()
}

/** 보관 중인 Okta 토큰 제거 (Knox 로그인/로그아웃 시 잔존 토큰 오염 방지). */
export const clearOktaTokens = (): void => {
	oktaAuth?.tokenManager.clear()
}

/**
 * LOGISPRD-9092 — Okta access JWT 를 sessionStorage 평문 미러(AuthStorage.accessToken)로 동기화하는 핸들러.
 * oktaAuth 싱글톤을 읽지 않아(미러 쓰기 side-effect 만) 단위 테스트가 쉽다. key 가 'accessToken' 일 때만 갱신.
 * LLD anchor: docs/lld/auth/LLD-9092-okta-token-mirror.md "## 핵심 로직 (의사 코드)".
 */
export const oktaTokenMirrorHandlers = {
	onAddedOrRenewed: (key: string, token: Token): void => {
		// key 매칭 + 토큰 형태('accessToken' 필드 존재로 AccessToken 좁힘) + authMode 가드.
		// authMode==='okta' 일 때만 미러 set — 로그아웃(authMode 제거) 후 autoRenew 가 잔존 SDK 토큰으로
		// 'renewed'/'added' 를 발화해도 미러를 재생성하지 않게 한다(seed 와 동일 기준). 정상 로그인 M1 은 콜백 후 리로드 seed 로 보장.
		if (key === 'accessToken' && 'accessToken' in token && AuthStorage.authMode.get() === 'okta') {
			AuthStorage.accessToken.set(token.accessToken)
		}
	},
	// removed → clear 를 microtask 로 지연 + read-through 가드. renew 는 동기적으로 removed(old)→added(new) 를
	// 연달아 발화하는데, microtask 시점엔 added 의 set(new) 가 이미 끝나 getOktaAccessToken() 이 new 를 반환하므로
	// clear 를 스킵한다 → 1.0 iframe(별도 event loop)이 보는 빈 윈도우(race)를 제거. 진짜 제거(logout/remove/
	// renew 영구 실패)는 토큰이 없으므로 clear 한다.
	onRemoved: (key: string): void => {
		if (key !== 'accessToken') return
		queueMicrotask(() => {
			if (!getOktaAccessToken()) AuthStorage.accessToken.clear()
		})
	},
}

/**
 * tokenManager 이벤트 구독으로 미러 set/clear 를 단일 지점에서 일원화한다.
 * 등록 직전 동일 핸들러를 off 로 떼고 on 으로 다시 붙여 중복 등록을 막는다(off-then-on 멱등 — HMR/재호출 안전).
 * 핸들러는 oktaTokenMirrorHandlers 싱글톤 객체의 메서드라 off/on 의 참조가 동일해 정확히 교체된다(모듈 스코프 가변 변수 불요).
 * 설치 SDK 7.8.0: clear()/remove()/renew 실패가 모두 'removed' 를 발화하므로 'removed' 구독 하나로
 * 로그아웃/Knox 전환/renew 실패의 모든 clear 경로가 자동 커버된다. (동작은 declared range ^7.8.0 의 minor bump 시 재확인 필요)
 */
export const subscribeOktaTokenMirror = (): void => {
	if (!oktaAuth) return
	const tm = oktaAuth.tokenManager
	tm.off('added', oktaTokenMirrorHandlers.onAddedOrRenewed)
	tm.on('added', oktaTokenMirrorHandlers.onAddedOrRenewed)
	tm.off('renewed', oktaTokenMirrorHandlers.onAddedOrRenewed)
	tm.on('renewed', oktaTokenMirrorHandlers.onAddedOrRenewed)
	tm.off('removed', oktaTokenMirrorHandlers.onRemoved)
	tm.on('removed', oktaTokenMirrorHandlers.onRemoved)
}

// 모듈 로드 시 1회 구독 등록 + seed. 콜백의 'added' 가 구독 등록 이전에 발화했을 경우(M1 누락) 대비해
// 현재 보관 중인 access token 을 1회 미러에 동기화한다(idempotent). 단 현재 세션이 okta 일 때만 —
// Knox/로그아웃 상태에서 localStorage 잔존 Okta 토큰이 미러로 부활하는 것을 차단(request.ts authMode 가드와 일관).
if (oktaAuth) {
	subscribeOktaTokenMirror()
	if (AuthStorage.authMode.get() === 'okta') {
		const seedToken = getOktaAccessToken()
		if (seedToken) AuthStorage.accessToken.set(seedToken)
	}
}
